import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Subscription } from '../types/models';
import { UsuarioPerfil } from '../types/enums/UsuarioPerfil';
import AuthService from '../services/AuthService';

interface AuthContextData {
  signed: boolean;
  user: User | null;
  subscription: Subscription | null;
  loading: boolean;
  signIn(email: string, senha: string): Promise<void>;
  signUp(nome: string, email: string, senha: string): Promise<void>;
  signOut(): void;
  updateUser(user: User): void;
  updateSubscription(subscription: Subscription): void;
  hasRole(role: string): boolean;
  updateUserInfo(user: User): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadStoredData() {
      const storedToken = AuthService.getStoredToken();
      
      if (storedToken) {
        try {
          AuthService.setAuthHeader(storedToken);
          
          // Request token renewal and get user data
          const userData = await AuthService.renewToken();
          
          setUser({
            guid: userData.guid,
            name: userData.name,
            email: userData.email,
            role: userData.role || UsuarioPerfil.EMPRESA_USUARIO,
            roles: userData.roles || []
          });
          
          // Set subscription and check if it's active
          const subscriptionData = userData.subscription || null;
          setSubscription(subscriptionData);
          
          // If subscription is not active, consider handling restricted access
          if (subscriptionData && !subscriptionData.hasActiveAccess) {
            navigate('/subscription');
          }
        } catch (error) {
          console.error('Failed to renew token or fetch user data:', error);
          // Optional: uncomment to clear invalid tokens
          // AuthService.signOut();
        }
      }
      
      setLoading(false);
    }

    loadStoredData();
  }, []);

  async function signIn(email: string, senha: string) {
    try {
      const { user: userData, subscription: subscriptionData } = await AuthService.signIn(email, senha);
      
      setUser(userData);
      setSubscription(subscriptionData);
      
      return;
    } catch (error) {
      throw error;
    }
  }
  
  async function signUp(nome: string, email: string, senha: string) {
    try {
      await AuthService.signUp(nome, email, senha);
      return;
    } catch (error) {
      throw error;
    }
  }

  function signOut() {
    AuthService.signOut();
    setUser(null);
    setSubscription(null);
    navigate('/tw/login');
  }
  
  function updateUser(updatedUser: User) {
    setUser(updatedUser);
  }
  
  function updateSubscription(updatedSubscription: Subscription) {
    setSubscription(updatedSubscription);
  }
  
  function hasRole(role: string): boolean {
    return user?.roles?.includes(role) || false;
  }

  const updateUserInfo = (updatedUser: User) => {
    setUser(updatedUser);
  };
  
  return (
    <AuthContext.Provider value={{ 
      signed: !!user, 
      user, 
      subscription,
      loading, 
      signIn, 
      signUp, 
      signOut, 
      updateUser, 
      updateSubscription,
      hasRole,
      updateUserInfo
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}
